import { Filter, FilterGroup } from '../page-component/PageTypes';
import { format, isValid } from 'date-fns';
import { Chip } from '@mui/material';
import { FieldValues, UseFormSetValue } from 'react-hook-form';
import { useFilter } from '@dispatch/shared/hooks';
import { MultiSelectOption } from '@dispatch/shared/types';
import { isObject } from '@turf/turf';
import { useMemo } from 'react';
import {
  buildContextKeyMap,
  flattenContextFilters,
} from './filterContextUtils';

export const SelectedChips = ({
  filters,
  filterLookUp,
  defaultFilters,
  setValue,
  filterGroups,
}: {
  filters: Filter[];
  filterLookUp: Record<string, any>;
  defaultFilters: Record<string, unknown>;
  setValue: UseFormSetValue<FieldValues>;
  filterGroups?: FilterGroup[];
}) => {
  const filterContext = useFilter();

  const flatContextFilters = useMemo(() => {
    if (!filterContext?.filters) return filterContext?.filters;
    if (!filterGroups) return filterContext.filters;
    return flattenContextFilters(filterContext.filters, filterGroups);
  }, [filterContext?.filters, filterGroups]);

  const handleArray = (value: unknown, filter: [string, Filter]) => {
    const titles = filterLookUp[filter[0]]?.options.flatMap(
      (option: { label: string; title: string; value: any }) => {
        if (Array.isArray(value) && value.includes(option.value)) {
          return option.title;
        }
        return [];
      }
    );

    value = titles.join(', ');
    return value;
  };

  const handleDate = (value: any, label: string) => {
    value = `${format(new Date(value[0]), 'MM/dd/yyyy')} - ${format(
      new Date(value[1]),
      'MM/dd/yyyy'
    )}`;

    label = label.includes('Date')
      ? label.replace('Date', '').replace(' ', '')
      : label;
    return [label, value];
  };

  const handleMultiAsyncSelect = (value: unknown) => {
    const multiSelectOptions = value as MultiSelectOption[];

    const resourceNames = multiSelectOptions.map((option) => option.title);

    return resourceNames.join(', ');
  };

  const handleLabelAndValue = (
    value: unknown,
    label: string,
    filter: [string, Filter]
  ) => {
    if (filterLookUp[filter[0]]?.options) {
      if (
        Array.isArray(value) &&
        filterLookUp[filter[0]]?.optionLookup?.[`${filter[1]}`] !== 'All'
      ) {
        // we parse states differently because we want the abbreviations instead of the full state
        if (label.toLowerCase().includes('states')) {
          value = value.join(', ');
        } else if (filterLookUp[filter[0]]?.type === 'async-select') {
          value = handleMultiAsyncSelect(value);
        } else {
          value = handleArray(value, filter);
        }
      } else {
        value = filterLookUp[filter[0]]?.optionLookup?.[`${filter[1]}`];
      }
    } else {
      if (Array.isArray(value) && isValid(new Date(value[0]))) {
        const [newLabel, newValue] = handleDate(value, label);
        label = newLabel;
        value = newValue;
      }
    }

    return [value, label];
  };

  const deleteSelectedChip = (deletedFilter: string) => {
    if (filterContext?.filters) {
      const contextKeyMap = filterGroups
        ? buildContextKeyMap(filterGroups)
        : {};

      const setImmutableValue = (
        filters: Record<string, unknown>,
        key: string,
        val: unknown,
        ctxKey: string | undefined
      ): Record<string, unknown> => {
        if (ctxKey && filters[ctxKey] && typeof filters[ctxKey] === 'object') {
          return {
            ...filters,
            [ctxKey]: {
              ...(filters[ctxKey] as Record<string, unknown>),
              [key]: val,
            },
          };
        }
        return { ...filters, [key]: val };
      };

      let updatedFilters = setImmutableValue(
        filterContext.filters,
        deletedFilter,
        defaultFilters[deletedFilter],
        contextKeyMap[deletedFilter]
      );

      // Update the form field to match the cleared context value
      setValue(deletedFilter, defaultFilters[deletedFilter]);

      // Date-range filters are backed by three keys — the tuple (e.g. lossDate)
      // and derived scalars (lossDateFrom/lossDateTo) the API reads. Clear all
      // three so stale scalars don't leak into subsequent requests.
      const def = filterLookUp[deletedFilter];
      if (def?.type === 'date-range') {
        if (def.start) {
          updatedFilters = setImmutableValue(
            updatedFilters,
            def.start,
            defaultFilters[def.start],
            contextKeyMap[def.start]
          );
          setValue(def.start, defaultFilters[def.start]);
        }
        if (def.end) {
          updatedFilters = setImmutableValue(
            updatedFilters,
            def.end,
            defaultFilters[def.end],
            contextKeyMap[def.end]
          );
          setValue(def.end, defaultFilters[def.end]);
        }
      }

      if (deletedFilter === 'contingentCompanies') {
        updatedFilters = setImmutableValue(
          updatedFilters,
          'employmentType',
          defaultFilters['employmentType'],
          contextKeyMap['employmentType']
        );
        setValue('employmentType', defaultFilters['employmentType']);
      }
      if (deletedFilter === 'employmentType') {
        updatedFilters = setImmutableValue(
          updatedFilters,
          'contingentCompanies',
          defaultFilters['contingentCompanies'],
          contextKeyMap['contingentCompanies']
        );
        setValue('contingentCompanies', defaultFilters['contingentCompanies']);
      }

      filterContext.setFilters(updatedFilters);
    }
  };

  const validateFilterArray = (filter: [string, Filter]) => {
    const contextValue = flatContextFilters?.[filter[0]];
    if (filterLookUp[filter[0]]?.type === 'date-range') {
      if (
        (contextValue as any[])?.length <= 0 ||
        !(contextValue as any[])?.every(
          (value: string, index: number) =>
            Array.isArray(filter[1]) &&
            new Date(value).getDate() === new Date(filter[1][index]).getDate()
        )
      ) {
        return false;
      }
    } else if (
      (contextValue as any[])?.length <= 0 ||
      !(contextValue as any[])?.every((value: any, index: number) => {
        if (isObject(value) && Array.isArray(filter[1])) {
          return (
            filter[1][index] &&
            value?.title === filter[1][index]['title'] &&
            value?.value === filter[1][index]['value']
          );
        } else {
          return Array.isArray(filter[1]) && value === filter[1][index];
        }
      })
    ) {
      return false;
    }
    return true;
  };

  const renderSelectedFilterChips = () => {
    return Object.entries(filters).flatMap((filter) => {
      // filter[1] is the value of the filter. Object.entries returns a dictionary of the object [[key, value]];
      // Filter of a boolean value can be set to false and we need to show chip
      if (!filter[1] && filter[1] !== false) return [];
      else if (Array.isArray(filter[1])) {
        if (
          filter[1].length <= 0 ||
          filter[1].some((x) => x === null) ||
          (flatContextFilters && flatContextFilters[filter[0]] == null) ||
          (flatContextFilters &&
            Array.isArray(flatContextFilters[filter[0]]) &&
            !validateFilterArray(filter))
        )
          return [];
      } else if (
        flatContextFilters &&
        flatContextFilters[filter[0]] != filter[1]
      ) {
        return [];
      }

      const [value, label] = handleLabelAndValue(
        filter[1],
        filterLookUp[filter[0]]?.label,
        filter
      );

      // if no label exists for the filter, then it is not actually a filter. This happens when getting saved filters, because there is some data in the
      // returned data, that does not map to inputs.
      if (!label) return [];

      let displayValue = value;
      if (
        label === 'Resources' &&
        Array.isArray(filter[1]) &&
        filter[1].length > 1
      ) {
        displayValue =
          (value as string).split(',')[0] +
          ' +' +
          (filter[1].length - 1).toString();
      }

      const resetValue = defaultFilters[filter[0]];
      let labelText = `${label}: ${displayValue}`;
      const chipText = filterLookUp[filter[0]]?.chipLabel?.find(
        (x: { label: string; value: string }) => x.value === String(filter[1])
      );

      if (chipText) {
        labelText = chipText.label;
      }

      return (
        <Chip
          data-testid="selected-filter-chip"
          title={`${value}`}
          label={labelText}
          onDelete={() => {
            deleteSelectedChip(filter[0]);
            setValue(filter[0], resetValue);
          }}
          sx={{
            height: '1.75rem',
            backgroundColor: '#E0E0E0',
            verticalAlign: 'center',
            margin: '0 2.5px',
            padding: 0,
            marginBottom: '2px',
            fontSize: '0.8125rem',
            '& .MuiChip-deleteIcon': {
              fontSize: '1.125rem',
            },
          }}
        />
      );
    });
  };

  return <>{renderSelectedFilterChips()}</>;
};
